// config.js

import { ConfigInterface } from "./common/interfaces";

// Helper function to get the domain extension
const getDomainExtension = (): string | undefined => {
  if (!window.location.hostname.includes("kompare")) return undefined;
  const parts = window.location.hostname.split(".");
  return parts[parts.length - 1];
};

// Cloudflare Turnstile sitekeys (public by design). One widget per zone, shared by the
// kompare funnels: the production widget lists the `.kompare.hr` funnel hosts (its secret
// is travel-insurance-api's prod TURNSTILE_SECRET_KEY), the kompare.work widget the dev
// hosts (secret in the `cloudflare-turnstile` k8s secret), and the kompare.top widget the
// `putno-osiguranje.kompare.top` dev host (secret in travel-insurance-api's kompare-top
// TURNSTILE_SECRET_KEY). `putno.kompare.hr` /
// `putno-osiguranje.kompare.work` must be on those widgets' hostname lists — a token from
// a widget the backend does not hold the secret for fails siteverify. Everywhere else the
// always-pass test key, which renders a real widget that solves itself.
const TURNSTILE_TEST_SITE_KEY = "1x00000000000000000000AA";
const TURNSTILE_SITE_KEYS: Record<string, string> = {
  hr: "0x4AAAAAAEjAOHsUMjdHEUX1",
  work: "0x4AAAAAAEjtHXvGqJsw2keO",
  top: "0x4AAAAAAEj-remgBtczXS1k",
};

// Elastic APM RUM intake. Dev hosts report to the dev stack (ns `logging` on kompare-dev,
// Kibana at kibana.kompare.work) so test traffic never lands in production APM. The page
// origin must be in that server's `rum.allow_origins` or every beacon is a silent 403.
const APM_SERVER_URL_PRODUCTION = "https://apm-rum.kompare.hr/";
const APM_SERVER_URL_DEV = "https://apm-rum.kompare.work/";

const getEnvVars = (): ConfigInterface => {
  const domainExtension: string | undefined = getDomainExtension();
  if (!domainExtension) return {
    REACT_APP_IS_DEV: import.meta.env.VITE_REACT_APP_IS_DEV ?? '',
    REACT_APP_URL: import.meta.env.VITE_REACT_APP_URL ?? '',
    REACT_APP_API_URL: import.meta.env.VITE_REACT_APP_API_URL ?? '',
    REACT_APP_DMS_URL: import.meta.env.VITE_REACT_APP_DMS_URL ?? '',
    REACT_APP_KOMPARE_HOME: import.meta.env.VITE_REACT_APP_KOMPARE_HOME ?? '',
    REACT_APP_WSPAY_URL: import.meta.env.VITE_REACT_APP_WSPAY_URL ?? '',
    REACT_APP_ENVIRONMENT: import.meta.env.VITE_REACT_APP_ENVIRONMENT ?? '',
    REACT_APP_ADDRESS_SEARCH_URL: import.meta.env.VITE_REACT_APP_ADDRESS_SEARCH_URL ?? '',
    REACT_APP_TURNSTILE_SITE_KEY: import.meta.env.VITE_TURNSTILE_SITE_KEY || TURNSTILE_TEST_SITE_KEY,
    REACT_APP_APM_SERVER_URL: import.meta.env.VITE_APM_SERVER_URL || APM_SERVER_URL_DEV,
    PORT: 4040,
  };

  const baseUrl: string = domainExtension === "hr" ? "https://putno.kompare." : "https://putno-osiguranje.kompare.";
  const wsPayUrl: string = domainExtension === "hr" ? "https://form.wspay.biz/authorization.aspx" : "https://formtest.wspay.biz/authorization.aspx";
  const isDev: boolean = domainExtension !== "hr";

  return {
    REACT_APP_IS_DEV: isDev ? "true" : "false",
    REACT_APP_URL: `${baseUrl}${domainExtension}`,
    REACT_APP_API_URL: `https://travel-insurance-api.kompare.${domainExtension}`,
    REACT_APP_DMS_URL: `https://minio.kompare.hr`,
    REACT_APP_KOMPARE_HOME: `https://kompare.hr`,
    REACT_APP_WSPAY_URL: wsPayUrl,
    REACT_APP_ENVIRONMENT: domainExtension === "hr" ? "production" : domainExtension,
    REACT_APP_ADDRESS_SEARCH_URL: `https://public-api-proxy.kompare.hr`,
    REACT_APP_TURNSTILE_SITE_KEY: TURNSTILE_SITE_KEYS[domainExtension] ?? TURNSTILE_TEST_SITE_KEY,
    REACT_APP_APM_SERVER_URL: domainExtension === "hr" ? APM_SERVER_URL_PRODUCTION : APM_SERVER_URL_DEV,
    PORT: 3003,
  };
};

export default getEnvVars();
